import React, { useState, useEffect, useCallback } from 'react';
import { api, useAuth } from '../App';
import { CampaignPanel } from '../components/CampaignPanel';
import { SourceBadges } from '../components/SourceBadges';
import { MetricoolOrganicPanel } from '../components/MetricoolOrganicPanel';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import MetricCard from '../components/MetricCard';
import DashboardChart from '../components/DashboardChart';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { ArrowLeft, Calendar as CalendarIcon, FileText, Loader2, RefreshCw, Sparkles, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, startOfQuarter, subMonths, endOfMonth } from 'date-fns';

const DATE_PRESETS = [
  { label: 'Last 7 days', getDates: () => ({ start: subDays(new Date(), 7), end: new Date() }) },
  { label: 'Last 30 days', getDates: () => ({ start: subDays(new Date(), 30), end: new Date() }) },
  { label: 'Month to Date', getDates: () => ({ start: startOfMonth(new Date()), end: new Date() }) },
  { label: 'Previous Month', getDates: () => { const s = startOfMonth(subMonths(new Date(), 1)); return { start: s, end: endOfMonth(subMonths(new Date(), 1)) }; }},
  { label: 'Quarter to Date', getDates: () => ({ start: startOfQuarter(new Date()), end: new Date() }) },
];

function formatDate(d) { return format(d, 'yyyy-MM-dd'); }

export default function DashboardPage({ clientId, navigate }) {
  const { user } = useAuth();
  const isInternal = (user?.role === 'super_admin' || user?.role === 'internal') && !user?.impersonating;
  const [sourceInfo, setSourceInfo] = useState(null);
  const [organicSource, setOrganicSource] = useState('metricool');
  const [metricsError, setMetricsError] = useState('');
  const [sourceError, setSourceError] = useState('');
  const [facebookSource, setFacebookSource] = useState('metricool');

  useEffect(() => {
    let active = true;
    if (!isInternal || !clientId) { setSourceInfo(null); return; }
    api('/api/data-sources/clients').then(result => {
      if (active) { setSourceInfo(result.clients.find(c => c.client_id === clientId) || null); setSourceError(''); }
    }).catch(e => { if (active) setSourceError(e.message); });
    return () => { active = false; };
  }, [clientId, isInternal]);
  useEffect(() => { setFacebookSource('metricool'); }, [clientId]);
  const effectiveClientId = clientId || user?.client_id;
  const [client, setClient] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: subDays(new Date(), 30),
    end: subDays(new Date(), 1)
  });
  const [compareEnabled, setCompareEnabled] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Report generation dialog
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportType, setReportType] = useState('comparison');
  const [customCompStart, setCustomCompStart] = useState('');
  const [customCompEnd, setCustomCompEnd] = useState(''); // 'comparison' or 'single'

  const requestRef = React.useRef(0);
  const fetchData = useCallback(async () => {
    const ticket = ++requestRef.current;
    if (!effectiveClientId) {setLoading(false);return;}
    setLoading(true);setMetricsError('');
    try {
      const compDays = Math.round((dateRange.end - dateRange.start) / (1000*60*60*24)) + 1;
      const [clientData, metricsData] = await Promise.all([
        api(`/api/clients/${effectiveClientId}`),
        api(`/api/clients/${effectiveClientId}/metrics?source=${organicSource}&start_date=${formatDate(dateRange.start)}&end_date=${formatDate(dateRange.end)}${
          compareEnabled ? `&comp_start=${formatDate(subDays(dateRange.start, compDays))}&comp_end=${formatDate(subDays(dateRange.end, compDays))}` : ''
        }`)
      ]);
      if(ticket===requestRef.current){setClient(clientData);setMetrics(metricsData);}
    } catch (err) {
      if(ticket===requestRef.current){setMetrics(null);setMetricsError(err.message||'Failed to load dashboard data');}
    } finally {
      if(ticket===requestRef.current)setLoading(false);
    }
  }, [effectiveClientId, dateRange, compareEnabled, organicSource]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePreset = (preset) => {
    const dates = preset.getDates();
    setDateRange(dates);
    setCalendarOpen(false);
  };

  const openReportDialog = () => {
    setReportType(compareEnabled ? 'comparison' : 'single');
    setReportDialogOpen(true);
  };

  // Report generation runs in background — user can navigate freely
  const handleGenerateReport = async () => {
    setGenerating(true);
    setReportDialogOpen(false);
    toast.info('Generating report with AI... This takes about 30 seconds. You can navigate freely.');
    
    try {
      const compDays = Math.round((dateRange.end - dateRange.start) / (1000*60*60*24)) + 1;
      const payload = {
        client_id: effectiveClientId,
        date_range_start: formatDate(dateRange.start),
        date_range_end: formatDate(dateRange.end),
      };
      if (reportType === 'comparison') {
        payload.comparison_start = formatDate(subDays(dateRange.start, compDays));
        payload.comparison_end = formatDate(subDays(dateRange.end, compDays));
      } else if (reportType === 'custom' && customCompStart && customCompEnd) {
        payload.comparison_start = customCompStart;
        payload.comparison_end = customCompEnd;
      }
      
      // Use separate fetch so it doesn't interfere with other api() calls
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${backendUrl}/api/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Report generation failed' }));
        throw new Error(err.detail || 'Report generation failed');
      }
      
      const report = await res.json();
      if (report && report._id) {
        toast.success('Report ready! Click to open.', {
          duration: 10000,
          action: {
            label: 'Open Report',
            onClick: () => navigate('report-builder', { reportId: report._id, clientId: effectiveClientId }),
          },
        });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const enabledPlatforms = client?.platforms || ['facebook', 'instagram', 'linkedin', 'tiktok', 'youtube'];
  const platformLabel = (p) => ({ facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn', tiktok: 'TikTok', youtube: 'YouTube' }[p] || p);

  const org = metrics?.organic_summary || {};
  const paid = metrics?.paid_summary || {};
  const platforms = metrics?.platforms || {};
  const beehiiv = metrics?.beehiiv || null;
  const googleAds = metrics?.google_ads_summary || null;

  const nativeOrganic = (platform) => <MetricoolOrganicPanel data={metrics?.metricool} platform={platform} internal={isInternal} onSetup={()=>navigate('metricool')}/>;
  const renderOverviewTab = () => metrics?.data_source==='metricool' ? nativeOrganic() : (
    <div className="space-y-6 animate-fade-in-up">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Impressions" value={org.impressions?.current || 0} changePct={org.impressions?.change_pct || 0} />
        <MetricCard label="Engagements" value={org.engagements?.current || 0} changePct={org.engagements?.change_pct || 0} />
        <MetricCard label="Engagement Rate" value={org.engagement_rate?.current || 0} suffix="%" changePct={org.engagement_rate?.change_pct || 0} />
        <MetricCard label="Link Clicks" value={org.link_clicks?.current || 0} changePct={org.link_clicks?.change_pct || 0} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Follower Growth" value={org.follower_growth?.current || 0} prefix="+" changePct={org.follower_growth?.change_pct || 0} />
        <MetricCard label="Video Views" value={org.video_views?.current || 0} changePct={org.video_views?.change_pct || 0} />
        <MetricCard label="Paid Spend" value={paid.spend?.current || 0} prefix="$" changePct={paid.spend?.change_pct || 0} />
        <MetricCard label="Conversions" value={paid.conversions?.current || 0} changePct={paid.conversions?.change_pct || 0} />
      </div>
      {metrics?.date_labels && (
        <>
          <DashboardChart
            title="Impressions by Platform"
            chartType="area"
            data={Object.fromEntries(enabledPlatforms.filter(p => platforms[p]?.daily_impressions || platforms[p]?.daily_views).map(p => [p, platforms[p]?.daily_impressions || platforms[p]?.daily_views]))}
            dataKeys={enabledPlatforms.filter(p => platforms[p]?.daily_impressions || platforms[p]?.daily_views).map(p => ({ key: p, name: platformLabel(p) }))}
            dateLabels={metrics.date_labels}
            height={320}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <DashboardChart
              title="Engagements by Platform"
              chartType="line"
              data={Object.fromEntries(enabledPlatforms.filter(p => platforms[p]?.daily_engagements).map(p => [p, platforms[p]?.daily_engagements]))}
              dataKeys={enabledPlatforms.filter(p => platforms[p]?.daily_engagements).map(p => ({ key: p, name: platformLabel(p) }))}
              dateLabels={metrics.date_labels}
            />
            <DashboardChart
              title="Video Views"
              chartType="area"
              data={Object.fromEntries(enabledPlatforms.filter(p => platforms[p]?.daily_video_views || (p !== 'linkedin' && platforms[p]?.daily_views)).map(p => [p, platforms[p]?.daily_video_views || platforms[p]?.daily_views]))}
              dataKeys={enabledPlatforms.filter(p => platforms[p]?.daily_video_views || (p !== 'linkedin' && platforms[p]?.daily_views)).map(p => ({ key: p, name: platformLabel(p) }))}
              dateLabels={metrics.date_labels}
            />
          </div>
        </>
      )}

      {/* Top 3 Most Engaged Posts Across All Platforms */}
      {(() => {
        const allPosts = enabledPlatforms.flatMap(p => 
          (platforms[p]?.top_posts || []).map(post => ({ ...post, platform: p }))
        ).sort((a, b) => (b.engagements || 0) - (a.engagements || 0)).slice(0, 3);
        
        if (allPosts.length === 0) return null;
        
        const pColors = { facebook: '#1877F2', instagram: '#E4405F', linkedin: '#0A66C2', tiktok: '#000', youtube: '#FF0000' };
        
        return (
          <Card className="rounded-2xl border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
            <CardContent className="p-5">
              <h4 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1f2a4d' }}>
                Top 3 Most Engaged Posts
              </h4>
              <div className="space-y-3">
                {allPosts.map((post, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl border bg-white hover:shadow-sm transition-shadow">
                    <div className="flex-shrink-0 flex flex-col items-center gap-1">
                      <span className="w-7 h-7 rounded-full bg-[#1f2a4d] text-white text-xs flex items-center justify-center font-bold">{i + 1}</span>
                      <span className="w-5 h-5 rounded flex items-center justify-center text-white text-[8px] font-bold" style={{ background: pColors[post.platform] || '#666' }}>
                        {(platformLabel(post.platform) || '?')[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {post.perma_link ? (
                          <a href={post.perma_link} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:text-[#e01e25] hover:underline truncate">{post.title}</a>
                        ) : (
                          <span className="text-sm font-medium truncate">{post.title}</span>
                        )}
                        <Badge variant="secondary" className="text-[9px] flex-shrink-0">{post.type}</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span><strong className="text-foreground">{(post.engagements || 0).toLocaleString()}</strong> engagements</span>
                        <span><strong className="text-foreground">{(post.impressions || 0).toLocaleString()}</strong> impressions</span>
                        <span>{post.engagement_rate}% ER</span>
                        <span className="text-[10px]">{post.date}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );

  const renderOrganicTab = () => metrics?.data_source==='metricool' ? nativeOrganic() : (
    <div className="space-y-6 animate-fade-in-up">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Impressions" value={org.impressions?.current || 0} changePct={org.impressions?.change_pct || 0} />
        <MetricCard label="Engagements" value={org.engagements?.current || 0} changePct={org.engagements?.change_pct || 0} />
        <MetricCard label="Engagement Rate" value={org.engagement_rate?.current || 0} suffix="%" changePct={org.engagement_rate?.change_pct || 0} />
        <MetricCard label="Follower Growth" value={org.follower_growth?.current || 0} prefix="+" changePct={org.follower_growth?.change_pct || 0} />
      </div>
      {metrics?.date_labels && (
        <>
          <DashboardChart
            title="Organic Impressions by Platform"
            chartType="area"
            data={Object.fromEntries(enabledPlatforms.filter(p => platforms[p]?.daily_impressions || platforms[p]?.daily_views).map(p => [p, platforms[p]?.daily_impressions || platforms[p]?.daily_views]))}
            dataKeys={enabledPlatforms.filter(p => platforms[p]?.daily_impressions || platforms[p]?.daily_views).map(p => ({ key: p, name: platformLabel(p) }))}
            dateLabels={metrics.date_labels}
            height={320}
          />
          <DashboardChart
            title="Organic Engagements by Platform"
            chartType="line"
            data={Object.fromEntries(enabledPlatforms.filter(p => platforms[p]?.daily_engagements).map(p => [p, platforms[p]?.daily_engagements]))}
            dataKeys={enabledPlatforms.filter(p => platforms[p]?.daily_engagements).map(p => ({ key: p, name: platformLabel(p) }))}
            dateLabels={metrics.date_labels}
          />
        </>
      )}
    </div>
  );

  const renderPaidTab = () => (
    <CampaignPanel key={effectiveClientId} clientId={effectiveClientId} startDate={formatDate(dateRange.start)} endDate={formatDate(dateRange.end)} onSourceChange={setFacebookSource}>
    <div className="space-y-6 animate-fade-in-up">
      {/* Top metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Ad Spend" value={paid.spend?.current || 0} prefix="$" changePct={paid.spend?.change_pct || 0} />
        <MetricCard label="Results / Leads" value={paid.leads?.current || paid.conversions?.current || 0} changePct={paid.leads?.change_pct || 0} />
        <MetricCard label="Cost per Result" value={paid.cost_per_result?.current || paid.cost_per_conversion?.current || 0} prefix="$" changePct={paid.cost_per_result?.change_pct || 0} />
        <MetricCard label="Reach" value={paid.reach?.current || 0} changePct={paid.reach?.change_pct || 0} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Impressions" value={paid.impressions?.current || 0} changePct={paid.impressions?.change_pct || 0} />
        <MetricCard label="Clicks" value={paid.clicks?.current || 0} changePct={paid.clicks?.change_pct || 0} />
        <MetricCard label="CTR" value={paid.ctr?.current || 0} suffix="%" changePct={paid.ctr?.change_pct || 0} />
        <MetricCard label="CPC" value={paid.cpc?.current || 0} prefix="$" changePct={paid.cpc?.change_pct || 0} />
      </div>

      {/* Spend chart */}
      {paid.daily_spend && paid.date_labels && (
        <DashboardChart
          title="Daily Ad Spend"
          chartType="area"
          data={{ spend: paid.daily_spend }}
          dataKeys={[{ key: 'spend', name: 'Spend ($)' }]}
          dateLabels={paid.date_labels}
        />
      )}

      {/* Detailed metrics table */}
      <Card className="rounded-2xl border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        <CardContent className="p-5">
          <h4 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1f2a4d' }}>
            Facebook Ads — Detailed Breakdown
          </h4>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                <TableHead className="text-right">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[
                ['Ad Spend', `$${(paid.spend?.current || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`],
                ['Impressions', (paid.impressions?.current || 0).toLocaleString()],
                ['Reach', (paid.reach?.current || 0).toLocaleString()],
                ['Clicks', (paid.clicks?.current || 0).toLocaleString()],
                ['Link Clicks', (paid.link_clicks?.current || 0).toLocaleString()],
                ['Landing Page Views', (paid.landing_page_views?.current || 0).toLocaleString()],
                ['CTR', `${paid.ctr?.current || 0}%`],
                ['CPC', `$${paid.cpc?.current || 0}`],
                ['CPM', `$${paid.cpm?.current || 0}`],
                ['Results / Leads', (paid.leads?.current || paid.conversions?.current || 0).toLocaleString()],
                ['Cost per Result', `$${paid.cost_per_result?.current || paid.cost_per_conversion?.current || 0}`],
                ['Video Views', (paid.video_views?.current || 0).toLocaleString()],
                ['Post Engagements', (paid.post_engagements?.current || 0).toLocaleString()],
              ].filter(([, v]) => v !== '0' && v !== '$0' && v !== '0%' && v !== '$0.00').map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell className="font-medium">{label}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {isInternal && paid.data_source && (
            <p className="text-[10px] text-muted-foreground mt-3">Source: {paid.data_source === 'facebook_ads_api' ? 'Meta Marketing API (Facebook Ads)' : paid.data_source}</p>
          )}
        </CardContent>
      </Card>

    </div>
    </CampaignPanel>
  );

  const renderPlatformTab = (platformKey) => {
    if(metrics?.data_source==='metricool')return nativeOrganic(platformKey);
    const p = platforms[platformKey] || {};
    const platformLabel = platformKey.charAt(0).toUpperCase() + platformKey.slice(1);
    const hasOrganic = (p.impressions_organic?.current || 0) > 0;
    const hasPaid = (p.impressions_paid?.current || 0) > 0;
    return (
      <div className="space-y-6 animate-fade-in-up">
        {/* Main metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {p.impressions && <MetricCard label="Impressions" value={p.impressions.current} changePct={p.impressions.change_pct} />}
          {p.engagements && <MetricCard label="Engagements" value={p.engagements.current} changePct={p.engagements.change_pct} />}
          {p.engagement_rate && <MetricCard label="Engagement Rate" value={p.engagement_rate.current} suffix="%" changePct={p.engagement_rate.change_pct} />}
          {p.followers && <MetricCard label="Followers" value={p.followers.current} changePct={p.followers.change_pct} />}
          {p.views && <MetricCard label="Views" value={p.views.current} changePct={p.views.change_pct} />}
          {p.watch_time_hours && <MetricCard label="Watch Time" value={p.watch_time_hours.current} suffix="h" changePct={p.watch_time_hours.change_pct} />}
          {p.subscribers && <MetricCard label="Subscribers" value={p.subscribers.current} changePct={p.subscribers.change_pct} />}
          {p.video_views && <MetricCard label="Video Views" value={p.video_views.current} changePct={p.video_views.change_pct} />}
        </div>
        {p.date_labels && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {p.daily_impressions && (
              <DashboardChart
                title={`${platformLabel} Impressions`}
                chartType="area"
                data={{ impressions: p.daily_impressions }}
                dataKeys={[{ key: 'impressions', name: 'Impressions' }]}
                dateLabels={p.date_labels}
              />
            )}
            {(p.daily_engagements || p.daily_views) && (
              <DashboardChart
                title={`${platformLabel} ${p.daily_engagements ? 'Engagements' : 'Views'}`}
                chartType="area"
                data={{ metric: p.daily_engagements || p.daily_views }}
                dataKeys={[{ key: 'metric', name: p.daily_engagements ? 'Engagements' : 'Views' }]}
                dateLabels={p.date_labels}
              />
            )}
          </div>
        )}
        {p.non_follower_reach_pct && (
          <Card className="rounded-2xl border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
            <CardContent className="p-5">
              <h4 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Instagram Insights</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <span className="text-xs text-muted-foreground block mb-1">Non-follower Reach</span>
                  <span className="text-xl font-semibold" style={{ color: '#1f2a4d' }}>{p.non_follower_reach_pct}%</span>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <span className="text-xs text-muted-foreground block mb-1">Reels % of Views</span>
                  <span className="text-xl font-semibold" style={{ color: '#1f2a4d' }}>{p.reels_pct}%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Organic vs Paid breakdown */}
        {(hasOrganic || hasPaid) && (
          <Card className="rounded-2xl border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
            <CardContent className="p-5">
              <h4 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1f2a4d' }}>
                Organic vs Paid Breakdown
              </h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Metric</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    {hasOrganic && <TableHead className="text-right">Organic</TableHead>}
                    {hasPaid && <TableHead className="text-right">Paid</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Impressions</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{(p.impressions?.current || 0).toLocaleString()}</TableCell>
                    {hasOrganic && <TableCell className="text-right tabular-nums text-emerald-700">{(p.impressions_organic?.current || 0).toLocaleString()}</TableCell>}
                    {hasPaid && <TableCell className="text-right tabular-nums text-blue-700">{(p.impressions_paid?.current || 0).toLocaleString()}</TableCell>}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Engagements</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{(p.engagements?.current || 0).toLocaleString()}</TableCell>
                    {hasOrganic && <TableCell className="text-right tabular-nums text-emerald-700">{(p.engagements_organic?.current || 0).toLocaleString()}</TableCell>}
                    {hasPaid && <TableCell className="text-right tabular-nums text-blue-700">{(p.engagements_paid?.current || 0).toLocaleString()}</TableCell>}
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
        {/* Top 5 Posts */}
        {p.top_posts && p.top_posts.length > 0 && (
          <Card className="rounded-2xl border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
            <CardContent className="p-5">
              <h4 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1f2a4d' }}>
                Top {p.top_posts.length} Posts by Engagement
              </h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>Post</TableHead>
                    <TableHead className="text-center">Type</TableHead>
                    <TableHead className="text-right">Impressions</TableHead>
                    <TableHead className="text-right">Engagements</TableHead>
                    <TableHead className="text-right">ER</TableHead>
                    <TableHead className="text-right">Likes</TableHead>
                    <TableHead className="text-right">Comments</TableHead>
                    <TableHead className="text-right">Shares</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {p.top_posts.map((post, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <span className="w-5 h-5 rounded-full bg-[#1f2a4d] text-white text-[10px] flex items-center justify-center font-bold">{post.rank}</span>
                      </TableCell>
                      <TableCell>
                        {post.perma_link ? (
                          <a href={post.perma_link} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:text-[#e01e25] hover:underline transition-colors">{post.title}</a>
                        ) : (
                          <div className="text-sm font-medium">{post.title}</div>
                        )}
                        <div className="text-xs text-muted-foreground">{post.date}</div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="text-[10px]">{post.type}</Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{post.impressions?.toLocaleString()}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold" style={{ color: '#1f2a4d' }}>{post.engagements?.toLocaleString()}</TableCell>
                      <TableCell className="text-right tabular-nums">{post.engagement_rate}%</TableCell>
                      <TableCell className="text-right tabular-nums">{post.likes?.toLocaleString()}</TableCell>
                      <TableCell className="text-right tabular-nums">{post.comments?.toLocaleString()}</TableCell>
                      <TableCell className="text-right tabular-nums">{post.shares?.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  if (loading) return <div className="space-y-4" data-testid="dashboard-loading"><Skeleton className="h-20"/><div className="grid sm:grid-cols-3 gap-4"><Skeleton className="h-32"/><Skeleton className="h-32"/><Skeleton className="h-32"/></div><Skeleton className="h-64"/></div>;
  if(metricsError)return <Alert data-testid="dashboard-load-error"><AlertTitle>Couldn’t load reporting</AlertTitle><AlertDescription>{metricsError}<Button variant="outline" className="block mt-3 min-h-11" onClick={fetchData} data-testid="dashboard-retry">Retry</Button></AlertDescription></Alert>;

  return (
    <div className="animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          {isInternal && (
            <Button variant="ghost" size="icon" onClick={() => navigate('clients')} data-testid="back-to-clients">
              <ArrowLeft size={18} />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {client?.name || 'Dashboard'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {formatDate(dateRange.start)} to {formatDate(dateRange.end)}
              {compareEnabled && ' (with comparison)'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="date-range-picker">
                <CalendarIcon size={14} className="mr-2" />
                {format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-2rem)] sm:w-auto max-w-[calc(100vw-2rem)] max-h-[85dvh] overflow-y-auto p-4" align="end" data-testid="dashboard-date-popover">
              <div className="space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  {DATE_PRESETS.map(preset => (
                    <Button key={preset.label} variant="outline" size="sm" className="text-xs min-h-11" data-testid={`dashboard-preset-${preset.label.toLowerCase().replaceAll(' ', '-')}`} onClick={() => handlePreset(preset)}>
                      {preset.label}
                    </Button>
                  ))}
                </div>
                <Separator />
                <Calendar
                  mode="range"
                  selected={{ from: dateRange.start, to: dateRange.end }}
                  onSelect={(range) => {
                    if (range?.from) {
                      setDateRange({ start: range.from, end: range.to || range.from });
                    }
                    // Only close when both dates are selected and they differ
                    if (range?.from && range?.to && range.from.getTime() !== range.to.getTime()) {
                      setCalendarOpen(false);
                    }
                  }}
                  numberOfMonths={2}
                  defaultMonth={dateRange.start}
                />
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant={compareEnabled ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCompareEnabled(!compareEnabled)}
            data-testid="compare-period-toggle"
            style={compareEnabled ? { background: '#1f2a4d' } : {}}
          >
            Compare
          </Button>

          <Button variant="outline" size="icon" onClick={fetchData} className="h-8 w-8">
            <RefreshCw size={14} />
          </Button>

          {isInternal && (
            <Button
              onClick={openReportDialog}
              disabled={generating}
              data-testid="generate-report-button"
              className="px-6 py-2.5 text-sm font-semibold shadow-lg hover:shadow-xl transition-shadow"
              style={{ background: '#e01e25', fontSize: '14px' }}
            >
              {generating ? (
                <><Loader2 size={16} className="mr-2 animate-spin" /> Generating Report...</>
              ) : (
                <><Sparkles size={16} className="mr-2" /> Generate Report</>
              )}
            </Button>
          )}
        </div>
      </div>

      {isInternal && <Card className="mb-6 border-primary/20 bg-primary/5" data-testid="dashboard-source-panel"><CardContent className="p-4 space-y-3"><div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3"><div><p className="text-xs font-medium text-muted-foreground" data-testid="dashboard-source-internal-label">Reporting source</p><p className="text-sm font-semibold mt-1" data-testid="dashboard-source-current">Viewing: {activeTab==='paid' ? facebookSource==='metricool'?'Metricool · Facebook Ads':'Direct Meta · stored history' : activeTab==='google-ads'?'Google Ads · direct':activeTab==='newsletter'?'Beehiiv newsletters':organicSource==='legacy'?'Legacy stored history · read-only':'Metricool · organic reporting'}</p></div><div className="flex flex-wrap gap-2"><Button variant="outline" className="min-h-11" onClick={()=>setOrganicSource(s=>s==='metricool'?'legacy':'metricool')} data-testid="dashboard-legacy-toggle">{organicSource==='metricool'?'View legacy history':'Use Metricool'}</Button><Button variant="outline" className="min-h-11" onClick={()=>navigate('metricool')} data-testid="dashboard-open-sources">Sync & coverage<ChevronRight size={14} className="ml-1"/></Button></div></div><SourceBadges info={sourceInfo} testPrefix="dashboard-source"/>{sourceError && <p className="text-xs text-destructive" data-testid="dashboard-source-error">Source details unavailable: {sourceError}</p>}{organicSource==='legacy'&&<p className="text-xs text-muted-foreground" data-testid="dashboard-legacy-note">Historical cache only. No new Sprout or direct Meta data is being fetched. New reports use Metricool.</p>}</CardContent></Card>}

      {/* Tabs — only show services that are turned ON in client config */}
      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="dashboard-platform-tabs">
        <TabsList className="mb-6 flex flex-wrap justify-start h-auto gap-1 [&>button]:min-h-11">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {enabledPlatforms.length > 0 && <TabsTrigger value="organic">Organic</TabsTrigger>}
          {(client?.paid_services || []).includes('facebook_ads') && <TabsTrigger value="paid" data-testid="dashboard-tab-facebook-ads">FB Ads</TabsTrigger>}
          {enabledPlatforms.map(p => (
            <TabsTrigger key={p} value={p}>{platformLabel(p)}</TabsTrigger>
          ))}
          {beehiiv && client?.beehiiv_enabled && <TabsTrigger value="newsletter">Newsletter</TabsTrigger>}
          {(client?.paid_services || []).includes('google_ads') && googleAds && <TabsTrigger value="google-ads">Google Ads</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview">{renderOverviewTab()}</TabsContent>
        {enabledPlatforms.length > 0 && <TabsContent value="organic">{renderOrganicTab()}</TabsContent>}
        {(client?.paid_services || []).includes('facebook_ads') && <TabsContent value="paid">{renderPaidTab()}</TabsContent>}
        {enabledPlatforms.map(p => (
          <TabsContent key={p} value={p}>{renderPlatformTab(p)}</TabsContent>
        ))}

        {/* Newsletter (Beehiiv) Tab */}
        {beehiiv && client?.beehiiv_enabled && (
          <TabsContent value="newsletter">
            <div className="space-y-6 animate-fade-in-up">
              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard label="Subscribers" value={beehiiv.subscriber_count || 0} changePct={0} />
                <MetricCard label="Newsletters Sent" value={beehiiv.posts?.filter(p => p.status === 'confirmed').length || 0} changePct={0} />
                <MetricCard label="Total Opens" value={beehiiv.posts?.reduce((s, p) => s + (p.opens || 0), 0) || 0} changePct={0} />
                <MetricCard label="Total Clicks" value={beehiiv.posts?.reduce((s, p) => s + (p.clicks || 0), 0) || 0} changePct={0} />
              </div>

              {/* Newsletter list */}
              <Card className="rounded-2xl border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
                <CardContent className="p-5">
                  <h4 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1f2a4d' }}>
                    Newsletter Editions
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-right">Recipients</TableHead>
                        <TableHead className="text-right">Opens</TableHead>
                        <TableHead className="text-right">Open Rate</TableHead>
                        <TableHead className="text-right">Clicks</TableHead>
                        <TableHead className="text-right">Click Rate</TableHead>
                        <TableHead className="text-right">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(beehiiv.posts || []).filter(p => p.status !== 'draft').map((post, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium text-sm max-w-[250px] truncate">
                            {post.web_url ? (
                              <a href={post.web_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#e01e25] hover:underline">{post.title}</a>
                            ) : post.title}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="text-[10px] capitalize">{post.status}</Badge>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{post.recipients?.toLocaleString() || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{post.opens?.toLocaleString() || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{post.open_rate > 0 ? `${post.open_rate}%` : '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{post.clicks?.toLocaleString() || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{post.click_rate > 0 ? `${post.click_rate}%` : '—'}</TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">{post.publish_date || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <p className="text-[10px] text-muted-foreground mt-3">Source: Beehiiv — {beehiiv.publication?.name || 'Newsletter'}</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* Google Ads Tab */}
        {(client?.paid_services || []).includes('google_ads') && googleAds && (
          <TabsContent value="google-ads">
            <div className="space-y-6 animate-fade-in-up">
              {/* Metric cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard label="Ad Spend" value={googleAds.spend?.current || 0} prefix="$" changePct={googleAds.spend?.change_pct || 0} />
                <MetricCard label="Conversions" value={googleAds.conversions?.current || 0} changePct={googleAds.conversions?.change_pct || 0} />
                <MetricCard label="Cost / Conversion" value={googleAds.cost_per_conversion?.current || 0} prefix="$" changePct={googleAds.cost_per_conversion?.change_pct || 0} />
                <MetricCard label="Clicks" value={googleAds.clicks?.current || 0} changePct={googleAds.clicks?.change_pct || 0} />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard label="Impressions" value={googleAds.impressions?.current || 0} changePct={googleAds.impressions?.change_pct || 0} />
                <MetricCard label="CTR" value={googleAds.ctr?.current || 0} suffix="%" changePct={googleAds.ctr?.change_pct || 0} />
                <MetricCard label="CPC" value={googleAds.cpc?.current || 0} prefix="$" changePct={googleAds.cpc?.change_pct || 0} />
              </div>

              {/* Daily spend chart */}
              {googleAds.daily_spend && googleAds.date_labels && (
                <DashboardChart
                  title="Daily Google Ads Spend"
                  chartType="area"
                  data={{ spend: googleAds.daily_spend }}
                  dataKeys={[{ key: 'spend', name: 'Spend ($)' }]}
                  dateLabels={googleAds.date_labels}
                />
              )}

              {/* Detailed table */}
              <Card className="rounded-2xl border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
                <CardContent className="p-5">
                  <h4 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1f2a4d' }}>
                    Google Ads — Detailed Breakdown
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Metric</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[
                        ['Ad Spend', `$${(googleAds.spend?.current || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`],
                        ['Impressions', (googleAds.impressions?.current || 0).toLocaleString()],
                        ['Clicks', (googleAds.clicks?.current || 0).toLocaleString()],
                        ['CTR', `${googleAds.ctr?.current || 0}%`],
                        ['CPC', `$${googleAds.cpc?.current || 0}`],
                        ['Conversions', (googleAds.conversions?.current || 0).toLocaleString()],
                        ['Cost / Conversion', `$${googleAds.cost_per_conversion?.current || 0}`],
                      ].filter(([, v]) => v !== '0' && v !== '$0' && v !== '0%' && v !== '$0.00').map(([label, value]) => (
                        <TableRow key={label}>
                          <TableCell className="font-medium">{label}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">{value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <p className="text-[10px] text-muted-foreground mt-3">Source: Google Ads API</p>
                </CardContent>
              </Card>

              {/* Campaign Breakdown */}
              {googleAds.campaigns && googleAds.campaigns.length > 0 && (
                <Card className="rounded-2xl border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
                  <CardContent className="p-5">
                    <h4 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1f2a4d' }}>
                      Campaigns ({googleAds.campaigns.length})
                    </h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Campaign</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-right">Spend</TableHead>
                          <TableHead className="text-right">Impressions</TableHead>
                          <TableHead className="text-right">Clicks</TableHead>
                          <TableHead className="text-right">CTR</TableHead>
                          <TableHead className="text-right">CPC</TableHead>
                          <TableHead className="text-right">Conversions</TableHead>
                          <TableHead className="text-right">Cost/Conv</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {googleAds.campaigns.map((c, i) => (
                          <TableRow key={i} className={c.status === 'paused' ? 'opacity-60' : ''}>
                            <TableCell className="font-medium text-sm max-w-[220px] truncate">{c.campaign_name}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className={`text-[9px] capitalize ${c.status === 'enabled' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                                {c.status || '—'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">${c.spend?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                            <TableCell className="text-right tabular-nums">{c.impressions?.toLocaleString()}</TableCell>
                            <TableCell className="text-right tabular-nums">{c.clicks?.toLocaleString()}</TableCell>
                            <TableCell className="text-right tabular-nums">{c.ctr}%</TableCell>
                            <TableCell className="text-right tabular-nums">${c.cpc}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold" style={{ color: c.conversions > 0 ? '#16a34a' : '#666' }}>{c.conversions || '—'}</TableCell>
                            <TableCell className="text-right tabular-nums">{c.cost_per_conversion > 0 ? `$${c.cost_per_conversion}` : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Generate Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="generate-report-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Generate Report</DialogTitle>
            <p className="text-sm text-muted-foreground">Configure your report before generating</p>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Reporting Period */}
            <div className="p-4 rounded-xl bg-muted/50 border">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Reporting Period</h4>
              <p className="text-sm font-medium">{format(dateRange.start, 'MMM d, yyyy')} – {format(dateRange.end, 'MMM d, yyyy')}</p>
            </div>

            {/* Report Type */}
            <div className="space-y-2.5">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Comparison</h4>
              {[
                { key: 'single', label: 'No Comparison', desc: 'Report on just the selected date range' },
                { key: 'comparison', label: 'Previous Period', desc: `Auto: ${format(subDays(dateRange.start, Math.round((dateRange.end - dateRange.start) / 86400000)), 'MMM d')} – ${format(subDays(dateRange.end, Math.round((dateRange.end - dateRange.start) / 86400000)), 'MMM d, yyyy')}` },
                { key: 'custom', label: 'Custom Period', desc: 'Choose any period to compare against' },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setReportType(opt.key)}
                  className={`w-full text-left p-3.5 rounded-xl border-2 transition-colors ${
                    reportType === opt.key ? 'border-[#e01e25] bg-red-50/30' : 'border-transparent bg-muted/40 hover:bg-muted/60'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${reportType === opt.key ? 'border-[#e01e25]' : 'border-gray-300'}`}>
                      {reportType === opt.key && <div className="w-2 h-2 rounded-full bg-[#e01e25]" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="text-xs text-muted-foreground">{opt.desc}</div>
                    </div>
                  </div>
                </button>
              ))}

              {/* Custom comparison date pickers */}
              {reportType === 'custom' && (
                <div className="p-4 rounded-xl border bg-white space-y-3">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase">Compare Against</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs text-muted-foreground">From</label>
                      <input
                        type="date"
                        className="w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                        value={customCompStart}
                        onChange={(e) => setCustomCompStart(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs text-muted-foreground">To</label>
                      <input
                        type="date"
                        className="w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                        value={customCompEnd}
                        onChange={(e) => setCustomCompEnd(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 rounded-lg bg-blue-50/50 border border-blue-100">
              <p className="text-xs text-blue-800">
                <Sparkles size={12} className="inline mr-1" />
                AI will generate executive summary, highlights, opportunities, and platform insights. You can edit everything before finalizing.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleGenerateReport} disabled={generating} data-testid="confirm-generate-report"
              className="px-6 shadow-lg" style={{ background: '#e01e25' }}>
              {generating ? <><Loader2 size={14} className="mr-2 animate-spin" /> Generating...</> : <><Sparkles size={14} className="mr-2" /> Generate Report</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
